import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Loading,
  Select,
  StatusBadge,
  Tooltip,
} from '../components/ui';
import { AnalysisBlock } from './ProspectDetail';
import { api, type Reply } from '../lib/api';
import { channelLabel, dateTime, humanize } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

export function Replies() {
  const [intent, setIntent] = useState('');
  const [needsAction, setNeedsAction] = useState(true);

  const params = new URLSearchParams({ limit: '100' });
  if (intent) params.set('intent', intent);
  if (needsAction) params.set('needs_action', 'true');
  const replies = useApi<{ replies: Reply[] }>(`/api/replies?${params.toString()}`);

  const sync = useAction(async () => {
    await api.post('/api/replies/sync-gmail');
    replies.reload();
  });

  const items = replies.data?.replies ?? [];

  return (
    <>
      <PageHeader
        title="Replies"
        description="What came back, and what Claude thinks you should do about it."
        helpHref="replies"
        actions={
          <>
            <div className="w-44">
              <Select value={intent} onChange={(event) => setIntent(event.target.value)}>
                <option value="">All intents</option>
                <option value="interested">Interested</option>
                <option value="warm">Warm</option>
                <option value="objection">Objection</option>
                <option value="waiting">Waiting</option>
                <option value="cold">Cold</option>
                <option value="unclear">Unclear</option>
              </Select>
            </div>
            <Tooltip content="Toggle between replies still waiting on a decision and every reply, including ones already handled.">
              <Button onClick={() => setNeedsAction((value) => !value)}>
                {needsAction ? 'Showing: needs action' : 'Showing: all'}
              </Button>
            </Tooltip>
            <Button variant="primary" loading={sync.pending} onClick={() => void sync.run()}>
              Sync Gmail
            </Button>
          </>
        }
      />

      {sync.error && <div className="mb-3"><ErrorNote message={sync.error} /></div>}
      {replies.loading && <Loading />}
      {replies.error && <ErrorNote message={replies.error} onRetry={replies.reload} />}

      {!replies.loading && items.length === 0 && (
        <EmptyState
          title={needsAction ? 'Nothing waiting on you' : 'No replies yet'}
          description="Sync Gmail to pull in responses to your outreach threads, or log one manually from a prospect page."
        />
      )}

      <div className="space-y-3">
        {items.map((reply) => (
          <ReplyCard key={reply.id} reply={reply} onChanged={replies.reload} />
        ))}
      </div>
    </>
  );
}

function ReplyCard({ reply, onChanged }: { reply: Reply; onChanged: () => void }) {
  const act = useAction(async (action: string, draftNow: boolean) => {
    await api.post(`/api/replies/${reply.id}/action`, { action, draft_now: draftNow });
    onChanged();
  });

  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Link
            to={`/prospects/${reply.prospect_id}`}
            className="font-semibold text-ink-900 hover:text-accent-700"
          >
            {reply.prospect_name}
          </Link>
          <span className="font-normal text-ink-500">{reply.company_name}</span>
          <StatusBadge status={reply.intent} />
          <Badge>{channelLabel(reply.channel)}</Badge>
          {reply.campaign_name && <Badge tone="blue">{reply.campaign_name}</Badge>}
          {reply.your_action && <Badge tone="purple">{humanize(reply.your_action)}</Badge>}
        </span>
      }
      action={<span className="text-xs text-ink-400">{dateTime(reply.received_at)}</span>}
    >
      <p className="whitespace-pre-wrap rounded-md bg-ink-50 p-3 text-sm text-ink-800">
        {reply.message_body}
      </p>

      {reply.claude_analysis && (
        <AnalysisBlock analysis={reply.claude_analysis} confidence={reply.confidence_score} />
      )}

      {act.error && <div className="mt-2"><ErrorNote message={act.error} /></div>}

      {!reply.action_taken_at && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={act.pending}
            onClick={() => void act.run('follow_up', true)}
          >
            Follow up & draft
          </Button>
          <Tooltip content="Flags this reply for manual handling instead of an automated follow-up.">
            <Button size="sm" loading={act.pending} onClick={() => void act.run('escalate', false)}>
              Escalate
            </Button>
          </Tooltip>
          <Tooltip content="Marks it seen without taking action now — it drops out of the needs-action view.">
            <Button size="sm" loading={act.pending} onClick={() => void act.run('manual_review', false)}>
              Handle later
            </Button>
          </Tooltip>
          <Button size="sm" loading={act.pending} onClick={() => void act.run('close_won', false)}>
            Won
          </Button>
          <Button size="sm" variant="danger" loading={act.pending} onClick={() => void act.run('close_lost', false)}>
            Lost
          </Button>
        </div>
      )}
    </Card>
  );
}
